/**
 * particleMorph.js - Haar-Morphing System für beliebige Texte
 *
 * Haare morphen von einer Buchstabenform zur nächsten.
 * Jeder Buchstabe wird aus einem GLB-Mesh geladen und als Punktwolke gesampelt.
 * Die Haar-Wurzeln bewegen sich zwischen den Buchstaben-Positionen.
 *
 * Unterstützt: A-Z, 0-9
 * Dateien: static/models/letters/A.glb, B.glb, ..., num_0.glb, num_1.glb, ...
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { scene } from "./scene.js";
import { settings, DAMPING, AMOUNT } from "./settings.js";
import { mapY, rotationImpulse, decayRotationImpulse } from "./input.js";

// Shader importieren (gleiche wie hair.js)
import vertexShader from "../shaders/hair.vert";
import fragmentShader from "../shaders/hair.frag";

// Konfigurierbarer Text
let textSequence = "OPEN121";  // Standard-Text (kann geändert werden)
let letterSequence = [];       // Aufgelöst: ["O", "P", "E", "N", "1", "2", "1"]

// Haar-System (statt Partikel)
let hairMesh = null;
let hairGeometry = null;
let uniforms = null;

// Positionen für jeden Buchstaben (Punktwolken)
const letterPositions = {};  // { "O": Float32Array, "P": Float32Array, ... }
const letterNormals = {};    // Normalen für Haar-Richtung
const letterMeshes = {};     // { "O": THREE.Mesh, ... } für sichtbare 3D-Modelle
const letterMeshData = {};   // { "O": { positionClone, normalClone, countVertices }, ... } für Vertex-Animation

// Haar-zu-Vertex Mapping für Synchronisation
const letterHairToVertexMap = {};  // { "O": Int32Array[hairIndex -> vertexIndex], ... }

// Aktuell sichtbares Letter-Mesh
let currentLetterMesh = null;
let currentMeshData = null;  // Vertex-Daten für aktuelles Mesh
let currentHairToVertexMap = null;  // Mapping für aktuellen Buchstaben

// Ziel-Mesh für Fade-Übergang beim Morphing
let targetLetterMesh = null;
let targetMeshData = null;

// Post-Morph Fade-In (neues Mesh blendet nach Morph langsam weiter ein)
let postMorphFadeProgress = 0;
let isPostMorphFading = false;

// Post-Morph Deformation Fade-In (Verformung blendet nach Morph langsam ein)
let deformFadeProgress = 1.0;  // 1.0 = voll, 0.0 = aus
let isDeformFading = false;
let isDeformFadingOut = false;  // Vor Morph: Deformation ausfaden
let pendingMorphIndex = -1;     // Wartender Morph-Index (startet nach Fade-Out)

// State
let currentLetterIndex = 0;
let targetLetterIndex = 0;
let morphProgress = 1.0;  // 1.0 = fertig, 0.0 = start
let isMorphing = false;
let isLoaded = false;
let loadedCount = 0;
let requiredLetters = [];  // Welche Buchstaben müssen geladen werden

// Settings
let HAIR_COUNT = 25000;
const SEGMENTS_PER_HAIR = 8;
const MORPH_DURATION = 1.2;  // Sekunden
const MORPH_SPEED = 1.0 / MORPH_DURATION;

// Haar-Settings
let HAIR_LENGTH = 0.12;
let HAIR_COLOR_ROOT = "#3D2314";
let HAIR_COLOR_TIP = "#5C3A21";
let BEND_STRENGTH = 0.4;
let GRAVITY_STRENGTH = 0.12;
let LETTER_SCALE = 0.7;

// Haar-Attribute
let rootPositionAttr = null;
let rootNormalAttr = null;
let velocityAttr = null;

// Aktuelle und Ziel-Positionen für Morphing
let currentRootPositions = null;  // Aktuelle Haar-Wurzel-Positionen
let currentRootNormals = null;    // Aktuelle Haar-Wurzel-Normalen
let startRootPositions = null;    // Start-Positionen für Morphing
let startRootNormals = null;      // Start-Normalen für Morphing

// Per-Haar Velocity für Animation
let hairVelocities = null;
let lastRootPositions = null;

// Zufällige Offsets
let hairPositionOffsets = null;
let hairNormalOffsets = null;
let hairLengthMultipliers = null;

// Partikel-Verzögerungen (für gestaffelten Effekt)
let morphDelays = null;

// Partikel-Kurven-Velocities (für Bogen-Bewegung)
let curveVelocities = null;

// Rotation
let currentRotationY = 0;

// Callback
let onLoadCallback = null;

/**
 * Konvertiert einen Text in ein Array von Buchstaben
 * Filtert ungültige Zeichen heraus
 */
function parseText(text) {
  const result = [];
  const upperText = text.toUpperCase();

  for (const char of upperText) {
    // Nur A-Z und 0-9 erlaubt
    if (/[A-Z0-9]/.test(char)) {
      result.push(char);
    }
    // Leerzeichen und andere Zeichen werden ignoriert
  }

  return result;
}

/**
 * Gibt den Dateipfad für einen Buchstaben zurück
 */
function getLetterPath(letter) {
  if (/[0-9]/.test(letter)) {
    return `models/letters/num_${letter}.glb`;
  }
  return `models/letters/${letter}.glb`;
}

/**
 * Setzt den Text der animiert werden soll
 * Kann vor oder nach dem Laden aufgerufen werden
 */
export function setText(text) {
  textSequence = text;
  letterSequence = parseText(text);
  console.log(`Text set to: "${text}" -> Letters: [${letterSequence.join(", ")}]`);

  // Wenn schon geladen, prüfe ob neue Buchstaben nötig sind
  if (isLoaded) {
    const missingLetters = letterSequence.filter(l => !letterPositions[l]);
    if (missingLetters.length > 0) {
      console.log(`Need to load additional letters: ${missingLetters.join(", ")}`);
      loadAdditionalLetters(missingLetters);
    }
  }
}

/**
 * Lädt zusätzliche Buchstaben nach
 */
function loadAdditionalLetters(letters) {
  const loader = new GLTFLoader();
  let loaded = 0;

  letters.forEach((letter) => {
    if (letterPositions[letter]) {
      loaded++;
      return;  // Schon geladen
    }

    const path = getLetterPath(letter);

    loader.load(
      path,
      (gltf) => {
        let mesh = null;
        gltf.scene.traverse((child) => {
          if (child.isMesh && !mesh) {
            mesh = child;
          }
        });

        if (mesh) {
          const { positions, normals } = samplePointsFromMesh(mesh, HAIR_COUNT);
          letterPositions[letter] = positions;
          letterNormals[letter] = normals;
          console.log(`Loaded additional: ${letter}`);
        }

        loaded++;
      },
      null,
      (error) => {
        console.error(`Error loading ${letter}:`, error);
        letterPositions[letter] = new Float32Array(HAIR_COUNT * 3);
        letterNormals[letter] = new Float32Array(HAIR_COUNT * 3);
        loaded++;
      }
    );
  });
}

/**
 * Lädt alle benötigten Buchstaben-Meshes und extrahiert Punktwolken
 */
export function loadLetterMeshes(callback, text = "OPEN121") {
  const loader = new GLTFLoader();
  onLoadCallback = callback;

  // Hole Settings
  HAIR_COUNT = settings.particleMorphHairCount || 25000;
  HAIR_COLOR_ROOT = settings.particleMorphHairColorRoot || "#3D2314";
  HAIR_COLOR_TIP = settings.particleMorphHairColorTip || "#5C3A21";
  HAIR_LENGTH = settings.particleMorphHairLength || 0.12;
  BEND_STRENGTH = settings.particleMorphHairBendStrength || 0.4;
  GRAVITY_STRENGTH = settings.particleMorphHairGravityStrength || 0.12;
  LETTER_SCALE = settings.particleMorphLetterScale || 0.7;

  // Text parsen
  setText(text);

  // Einzigartige Buchstaben finden
  requiredLetters = [...new Set(letterSequence)];
  loadedCount = 0;

  console.log(`Loading letter meshes for: "${text}"`);
  console.log(`Required letters: ${requiredLetters.join(", ")}`);

  if (requiredLetters.length === 0) {
    console.error("No valid letters in text!");
    return;
  }

  requiredLetters.forEach((letter) => {
    const path = getLetterPath(letter);

    loader.load(
      path,
      (gltf) => {
        console.log(`Loaded: ${letter}`);

        // Finde das Mesh
        let mesh = null;
        gltf.scene.traverse((child) => {
          if (child.isMesh && !mesh) {
            mesh = child;
          }
        });

        if (mesh) {
          // Extrahiere Punktwolke aus dem Mesh
          const { positions, normals, hairToVertexMap } = samplePointsFromMesh(mesh, HAIR_COUNT);
          letterPositions[letter] = positions;
          letterNormals[letter] = normals;
          letterHairToVertexMap[letter] = hairToVertexMap;
          console.log(`  Sampled ${HAIR_COUNT} points from ${letter}`);

          // Mesh für optionale Anzeige speichern
          const clonedMesh = mesh.clone();
          // Geometrie klonen und Koordinaten transformieren (wie bei Haar-Punkten)
          const originalGeom = mesh.geometry;
          const newGeom = originalGeom.clone();

          // Transformiere Vertices direkt in der Geometrie (Blender → Three.js)
          const posArray = newGeom.attributes.position.array;
          const normArray = newGeom.attributes.normal ? newGeom.attributes.normal.array : null;
          const vertCount = newGeom.attributes.position.count;

          for (let v = 0; v < vertCount; v++) {
            const x = posArray[v * 3];
            const y = posArray[v * 3 + 1];
            const z = posArray[v * 3 + 2];

            // Gleiche Transformation wie bei Haar-Punkten
            posArray[v * 3] = x;           // X bleibt
            posArray[v * 3 + 1] = -z;      // Z → -Y
            posArray[v * 3 + 2] = y;       // Y → Z

            if (normArray) {
              const nx = normArray[v * 3];
              const ny = normArray[v * 3 + 1];
              const nz = normArray[v * 3 + 2];
              normArray[v * 3] = nx;
              normArray[v * 3 + 1] = -nz;
              normArray[v * 3 + 2] = ny;
            }
          }
          newGeom.attributes.position.needsUpdate = true;
          if (normArray) newGeom.attributes.normal.needsUpdate = true;
          newGeom.computeVertexNormals();

          clonedMesh.geometry = newGeom;
          clonedMesh.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(settings.particleMorphMeshColor || settings.particleMorphHairColorRoot || "#3D2314"),
            metalness: 0.3,
            roughness: 0.7,
            transparent: true,  // Für Fade-Übergang
            opacity: 1.0,
          });
          clonedMesh.scale.set(LETTER_SCALE, LETTER_SCALE, LETTER_SCALE);
          clonedMesh.visible = false;
          clonedMesh.name = `LetterMesh_${letter}`;
          scene.add(clonedMesh);
          letterMeshes[letter] = clonedMesh;

          // Vertex-Daten für Animation speichern (Kopie der Original-Positionen)
          const posClone = new Float32Array(posArray);
          const normClone = normArray ? new Float32Array(normArray) : null;
          letterMeshData[letter] = {
            positionClone: posClone,
            normalClone: normClone,
            countVertices: vertCount
          };
        } else {
          console.warn(`No mesh found in ${letter}.glb`);
          letterPositions[letter] = new Float32Array(HAIR_COUNT * 3);
          letterNormals[letter] = new Float32Array(HAIR_COUNT * 3);
        }

        loadedCount++;
        checkAllLoaded();
      },
      (progress) => {
        // Loading progress
      },
      (error) => {
        console.error(`Error loading ${path}:`, error);
        letterPositions[letter] = new Float32Array(HAIR_COUNT * 3);
        letterNormals[letter] = new Float32Array(HAIR_COUNT * 3);
        loadedCount++;
        checkAllLoaded();
      }
    );
  });
}

/**
 * Prüft ob alle Buchstaben geladen sind
 */
function checkAllLoaded() {
  if (loadedCount >= requiredLetters.length) {
    console.log("All letters loaded!");
    isLoaded = true;
    createHairSystem();

    // Erstes Letter-Mesh anzeigen wenn Setting aktiviert
    if (settings.particleMorphShowMesh && letterSequence.length > 0) {
      showLetterMesh(letterSequence[0]);
    }

    if (onLoadCallback) {
      onLoadCallback(hairMesh);
    }
  }
}

/**
 * Zeigt das Mesh für einen bestimmten Buchstaben an
 */
function showLetterMesh(letter) {
  // Alle anderen verstecken
  Object.values(letterMeshes).forEach(mesh => {
    if (mesh) mesh.visible = false;
  });

  // Gewünschtes Mesh anzeigen
  if (letterMeshes[letter] && settings.particleMorphShowMesh) {
    letterMeshes[letter].visible = true;
    currentLetterMesh = letterMeshes[letter];
    currentMeshData = letterMeshData[letter] || null;
    currentHairToVertexMap = letterHairToVertexMap[letter] || null;

    // Rotation vom Haar-System übernehmen
    if (hairMesh) {
      currentLetterMesh.rotation.y = hairMesh.rotation.y;
    }
  } else {
    currentLetterMesh = null;
    currentMeshData = null;
    currentHairToVertexMap = null;
  }
}

/**
 * Aktualisiert die Sichtbarkeit des Letter-Meshes
 */
export function setShowMesh(show) {
  settings.particleMorphShowMesh = show;
  if (show && letterSequence.length > 0) {
    showLetterMesh(letterSequence[currentLetterIndex]);
  } else {
    Object.values(letterMeshes).forEach(mesh => {
      if (mesh) mesh.visible = false;
    });
    currentLetterMesh = null;
    currentMeshData = null;
    currentHairToVertexMap = null;
  }
}

/**
 * Gibt das aktuelle Letter-Mesh zurück (für Dissolve)
 */
export function getCurrentLetterMesh() {
  return currentLetterMesh;
}

/**
 * Sampelt gleichmäßig verteilte Punkte von einem Mesh
 * Gibt auch ein Mapping von Haar-Index zu nächstem Vertex-Index zurück
 */
function samplePointsFromMesh(mesh, count) {
  const geometry = mesh.geometry;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const hairToVertexMap = new Int32Array(count);  // Welcher Vertex ist am nächsten

  // Berechne Flächen-gewichtetes Sampling
  const posAttr = geometry.attributes.position;
  const normAttr = geometry.attributes.normal;
  const index = geometry.index;

  const triangles = [];
  let totalArea = 0;

  // Sammle alle Dreiecke mit ihren Flächen
  if (index) {
    const indices = index.array;
    for (let i = 0; i < indices.length; i += 3) {
      const area = calculateTriangleArea(posAttr, indices[i], indices[i + 1], indices[i + 2]);
      totalArea += area;
      triangles.push({
        i0: indices[i],
        i1: indices[i + 1],
        i2: indices[i + 2],
        area,
        cumulativeArea: totalArea
      });
    }
  } else {
    const vertCount = posAttr.count;
    for (let i = 0; i < vertCount; i += 3) {
      const area = calculateTriangleArea(posAttr, i, i + 1, i + 2);
      totalArea += area;
      triangles.push({
        i0: i,
        i1: i + 1,
        i2: i + 2,
        area,
        cumulativeArea: totalArea
      });
    }
  }

  // Falls keine Dreiecke gefunden wurden
  if (triangles.length === 0 || totalArea === 0) {
    console.warn("No triangles found in mesh, using fallback positions");
    for (let p = 0; p < count; p++) {
      positions[p * 3] = (Math.random() - 0.5) * 2;
      positions[p * 3 + 1] = (Math.random() - 0.5) * 2;
      positions[p * 3 + 2] = (Math.random() - 0.5) * 0.5;
      normals[p * 3] = 0;
      normals[p * 3 + 1] = 0;
      normals[p * 3 + 2] = 1;
      hairToVertexMap[p] = 0;
    }
    return { positions, normals, hairToVertexMap };
  }

  // Sample Punkte
  for (let p = 0; p < count; p++) {
    // Wähle zufälliges Dreieck (gewichtet nach Fläche)
    const r = Math.random() * totalArea;
    let tri = triangles[0];

    // Binary search
    let low = 0, high = triangles.length - 1;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (triangles[mid].cumulativeArea < r) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    tri = triangles[low];

    // Zufälliger Punkt im Dreieck (barycentric)
    let u = Math.random();
    let v = Math.random();
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const w = 1 - u - v;

    // Speichere den Vertex mit dem höchsten Gewicht (nächsten Vertex)
    // Das wird für die Synchronisation der Haare mit der Mesh-Verformung verwendet
    if (w >= u && w >= v) {
      hairToVertexMap[p] = tri.i0;
    } else if (u >= v) {
      hairToVertexMap[p] = tri.i1;
    } else {
      hairToVertexMap[p] = tri.i2;
    }

    // Interpoliere Position
    const p0 = getVertex(posAttr, tri.i0);
    const p1 = getVertex(posAttr, tri.i1);
    const p2 = getVertex(posAttr, tri.i2);

    // WICHTIG: Koordinaten-Transformation für Blender → Three.js
    // Buchstaben standen auf dem Kopf, daher Y negieren
    positions[p * 3] = p0.x * w + p1.x * u + p2.x * v;       // X bleibt
    positions[p * 3 + 1] = -(p0.z * w + p1.z * u + p2.z * v); // Z → -Y (umgekehrt für richtige Orientierung)
    positions[p * 3 + 2] = p0.y * w + p1.y * u + p2.y * v;   // Y → Z

    // Interpoliere Normale (auch transformieren)
    if (normAttr) {
      const n0 = getVertex(normAttr, tri.i0);
      const n1 = getVertex(normAttr, tri.i1);
      const n2 = getVertex(normAttr, tri.i2);

      let nx = n0.x * w + n1.x * u + n2.x * v;       // X bleibt
      let ny = -(n0.z * w + n1.z * u + n2.z * v);    // Z → -Y
      let nz = n0.y * w + n1.y * u + n2.y * v;       // Y → Z

      // Normalisieren
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 0) {
        nx /= len; ny /= len; nz /= len;
      }

      normals[p * 3] = nx;
      normals[p * 3 + 1] = ny;
      normals[p * 3 + 2] = nz;
    } else {
      normals[p * 3] = 0;
      normals[p * 3 + 1] = 0;
      normals[p * 3 + 2] = 1;
    }
  }

  return { positions, normals, hairToVertexMap };
}

function getVertex(attr, index) {
  return {
    x: attr.getX(index),
    y: attr.getY(index),
    z: attr.getZ(index)
  };
}

function calculateTriangleArea(posAttr, i0, i1, i2) {
  const p0 = getVertex(posAttr, i0);
  const p1 = getVertex(posAttr, i1);
  const p2 = getVertex(posAttr, i2);

  // Kanten
  const ax = p1.x - p0.x, ay = p1.y - p0.y, az = p1.z - p0.z;
  const bx = p2.x - p0.x, by = p2.y - p0.y, bz = p2.z - p0.z;

  // Kreuzprodukt
  const cx = ay * bz - az * by;
  const cy = az * bx - ax * bz;
  const cz = ax * by - ay * bx;

  return 0.5 * Math.sqrt(cx * cx + cy * cy + cz * cz);
}

/**
 * Erstellt das Haar-System
 */
function createHairSystem() {
  if (letterSequence.length === 0) {
    console.error("No letters in sequence!");
    return;
  }

  // Initiale Positionen vom ersten Buchstaben
  const firstLetter = letterSequence[0];
  const initialPositions = letterPositions[firstLetter];
  const initialNormals = letterNormals[firstLetter];

  if (!initialPositions) {
    console.error(`No positions for first letter: ${firstLetter}`);
    return;
  }

  // Aktuelle Root-Positionen und Normalen
  currentRootPositions = new Float32Array(initialPositions);
  currentRootNormals = new Float32Array(initialNormals);
  startRootPositions = new Float32Array(HAIR_COUNT * 3);
  startRootNormals = new Float32Array(HAIR_COUNT * 3);

  // Morph-Verzögerungen (für gestaffelten Effekt)
  morphDelays = new Float32Array(HAIR_COUNT);
  for (let i = 0; i < HAIR_COUNT; i++) {
    morphDelays[i] = Math.random() * 0.3;  // 0-0.3s Verzögerung
  }

  // Kurven-Velocities für Bogen-Bewegung beim Morphing
  curveVelocities = new Float32Array(HAIR_COUNT * 3);

  // Zufällige Offsets
  hairPositionOffsets = new Float32Array(HAIR_COUNT * 3);
  hairNormalOffsets = new Float32Array(HAIR_COUNT * 3);
  hairLengthMultipliers = new Float32Array(HAIR_COUNT);

  // Velocity-Tracking
  hairVelocities = new Float32Array(HAIR_COUNT * 3);
  lastRootPositions = new Float32Array(HAIR_COUNT * 3);

  // Gesamtzahl der Vertices für alle Haare
  const totalVertices = HAIR_COUNT * SEGMENTS_PER_HAIR;

  // Arrays für Geometrie
  const positions = new Float32Array(totalVertices * 3);
  const rootPositions = new Float32Array(totalVertices * 3);
  const rootNormals = new Float32Array(totalVertices * 3);
  const velocities = new Float32Array(totalVertices * 3);
  const segments = new Float32Array(totalVertices);
  const randomOffsets = new Float32Array(totalVertices);
  const lengthMultipliers = new Float32Array(totalVertices);
  const indices = [];

  for (let hair = 0; hair < HAIR_COUNT; hair++) {
    const randomOffset = Math.random();

    // Kleine zufällige Position-Offsets
    hairPositionOffsets[hair * 3] = (Math.random() - 0.5) * 0.01;
    hairPositionOffsets[hair * 3 + 1] = (Math.random() - 0.5) * 0.01;
    hairPositionOffsets[hair * 3 + 2] = (Math.random() - 0.5) * 0.01;

    // Kleine zufällige Normal-Offsets
    hairNormalOffsets[hair * 3] = (Math.random() - 0.5) * 0.3;
    hairNormalOffsets[hair * 3 + 1] = (Math.random() - 0.5) * 0.3;
    hairNormalOffsets[hair * 3 + 2] = (Math.random() - 0.5) * 0.3;

    // Zufällige Länge: 0.6 bis 1.4
    hairLengthMultipliers[hair] = 0.6 + Math.random() * 0.8;

    // Initiale Position speichern
    lastRootPositions[hair * 3] = currentRootPositions[hair * 3];
    lastRootPositions[hair * 3 + 1] = currentRootPositions[hair * 3 + 1];
    lastRootPositions[hair * 3 + 2] = currentRootPositions[hair * 3 + 2];

    for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
      const idx = hair * SEGMENTS_PER_HAIR + seg;
      const segmentRatio = seg / (SEGMENTS_PER_HAIR - 1);

      // Initiale Positionen
      positions[idx * 3] = 0;
      positions[idx * 3 + 1] = 0;
      positions[idx * 3 + 2] = 0;

      // Root Position und Normal
      rootPositions[idx * 3] = currentRootPositions[hair * 3];
      rootPositions[idx * 3 + 1] = currentRootPositions[hair * 3 + 1];
      rootPositions[idx * 3 + 2] = currentRootPositions[hair * 3 + 2];

      rootNormals[idx * 3] = currentRootNormals[hair * 3];
      rootNormals[idx * 3 + 1] = currentRootNormals[hair * 3 + 1];
      rootNormals[idx * 3 + 2] = currentRootNormals[hair * 3 + 2];

      segments[idx] = segmentRatio;
      randomOffsets[idx] = randomOffset;
      lengthMultipliers[idx] = hairLengthMultipliers[hair];

      // Line-Indices
      if (seg < SEGMENTS_PER_HAIR - 1) {
        indices.push(idx, idx + 1);
      }
    }
  }

  // Geometrie erstellen
  hairGeometry = new THREE.BufferGeometry();
  hairGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  rootPositionAttr = new THREE.BufferAttribute(rootPositions, 3);
  rootPositionAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aRootPosition", rootPositionAttr);

  rootNormalAttr = new THREE.BufferAttribute(rootNormals, 3);
  rootNormalAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aRootNormal", rootNormalAttr);

  velocityAttr = new THREE.BufferAttribute(velocities, 3);
  velocityAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aVelocity", velocityAttr);

  hairGeometry.setAttribute("aSegment", new THREE.BufferAttribute(segments, 1));
  hairGeometry.setAttribute("aRandomOffset", new THREE.BufferAttribute(randomOffsets, 1));
  hairGeometry.setAttribute("aLengthMult", new THREE.BufferAttribute(lengthMultipliers, 1));

  hairGeometry.setIndex(indices);

  // Uniforms
  const gravityEnabled = settings.particleMorphHairGravity ? 1.0 : 0.0;

  uniforms = {
    uTime: { value: 0 },
    uMouseY: { value: 0 },
    uHairLength: { value: HAIR_LENGTH },
    uBendStrength: { value: BEND_STRENGTH },
    uColorRoot: { value: new THREE.Color(HAIR_COLOR_ROOT) },
    uColorTip: { value: new THREE.Color(HAIR_COLOR_TIP) },
    uGravityEnabled: { value: gravityEnabled },
    uGravityStrength: { value: GRAVITY_STRENGTH },
  };

  // Shader Material
  const hairMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  // LineSegments für die Haare
  hairMesh = new THREE.LineSegments(hairGeometry, hairMaterial);
  hairMesh.scale.set(LETTER_SCALE, LETTER_SCALE, LETTER_SCALE);
  hairMesh.name = "HairMorphSystem";

  scene.add(hairMesh);

  console.log(`Hair morph system created with ${HAIR_COUNT} hairs`);
  console.log(`Text sequence: ${letterSequence.join(" → ")}`);
}

/**
 * Bereitet Morph vor: Startet Deformation Fade-Out, dann Morph
 */
function prepareMorph(targetIndex) {
  // Wenn Deformation aktiv, erst ausfaden
  if (deformFadeProgress > 0.01 && settings.mouseDeform && settings.vertices) {
    pendingMorphIndex = targetIndex;
    isDeformFadingOut = true;
    isDeformFading = false;
    console.log("Fading out deformation before morph...");
  } else {
    // Keine Deformation aktiv, direkt morphen
    startMorphTo(targetIndex);
  }
}

/**
 * Morpht zum nächsten Buchstaben
 */
export function morphToNextLetter() {
  if (isMorphing || isDeformFadingOut || !isLoaded) return;
  if (letterSequence.length === 0) return;

  const nextIndex = (currentLetterIndex + 1) % letterSequence.length;
  prepareMorph(nextIndex);
}

/**
 * Morpht zum vorherigen Buchstaben
 */
export function morphToPreviousLetter() {
  if (isMorphing || isDeformFadingOut || !isLoaded) return;
  if (letterSequence.length === 0) return;

  const prevIndex = (currentLetterIndex - 1 + letterSequence.length) % letterSequence.length;
  prepareMorph(prevIndex);
}

/**
 * Morpht zu einem spezifischen Index
 */
export function morphToLetter(index) {
  if (isMorphing || isDeformFadingOut || !isLoaded) return;
  if (index < 0 || index >= letterSequence.length) return;
  if (index === currentLetterIndex) return;

  prepareMorph(index);
}

/**
 * Startet das Morphing (wird aufgerufen nachdem Deformation ausgeblendet ist)
 */
function startMorphTo(targetIndex) {
  targetLetterIndex = targetIndex;
  morphProgress = 0;
  isMorphing = true;

  // Post-Morph Fade abbrechen falls aktiv
  isPostMorphFading = false;
  isDeformFading = false;
  isDeformFadingOut = false;
  pendingMorphIndex = -1;

  // Speichere Start-Positionen und Normalen
  for (let i = 0; i < currentRootPositions.length; i++) {
    startRootPositions[i] = currentRootPositions[i];
    startRootNormals[i] = currentRootNormals[i];
  }

  // Berechne zufällige Kurven-Velocities für Bogen-Bewegung
  for (let i = 0; i < HAIR_COUNT; i++) {
    // Zufällige Auslenkung in der Mitte der Animation
    curveVelocities[i * 3] = (Math.random() - 0.5) * 0.4;
    curveVelocities[i * 3 + 1] = (Math.random() - 0.5) * 0.4 + 0.15;  // Leicht nach oben
    curveVelocities[i * 3 + 2] = (Math.random() - 0.5) * 0.4;
  }

  const fromLetter = letterSequence[currentLetterIndex];
  const toLetter = letterSequence[targetLetterIndex];
  console.log(`Morphing hair: ${fromLetter} → ${toLetter}`);

  // Mesh-Fade vorbereiten (wenn Meshes sichtbar)
  if (settings.particleMorphShowMesh) {
    // Ziel-Mesh vorbereiten
    targetLetterMesh = letterMeshes[toLetter] || null;
    targetMeshData = letterMeshData[toLetter] || null;

    if (targetLetterMesh) {
      // Ziel-Mesh sichtbar machen mit Opacity 0
      targetLetterMesh.visible = true;
      targetLetterMesh.material.opacity = 0;

      // Rotation vom aktuellen Mesh übernehmen
      if (currentLetterMesh) {
        targetLetterMesh.rotation.y = currentLetterMesh.rotation.y;
      } else if (hairMesh) {
        targetLetterMesh.rotation.y = hairMesh.rotation.y;
      }
    }
  }
}

/**
 * Update-Funktion (jeden Frame) - Wie Donut: Maus-Rotation + Vertex-Animation
 */
export function updateParticleMorph(elapsedTime, deltaTime) {
  if (!hairMesh || !isLoaded) return;

  const now = Date.now() / 300;

  // Zeit-Update für Shader
  if (uniforms) {
    uniforms.uTime.value = elapsedTime;
  }

  // Rotation wie Donut: Maus-Impulse + Basis-Rotation
  if (settings.rotation) {
    // Maus-Impulse anwenden
    hairMesh.rotation.y += rotationImpulse;
    decayRotationImpulse();

    // Basis-Rotation
    hairMesh.rotation.y += settings.rotationSpeed * deltaTime;

    // Letter-Mesh Rotation synchronisieren
    if (currentLetterMesh) {
      currentLetterMesh.rotation.y = hairMesh.rotation.y;
    }
  }

  // Vertex-Animation auf Letter-Mesh (wie Donut)
  // Speichert auch die Deformation für die Haar-Synchronisation
  let vertexDeformations = null;  // Array mit Vertex-Offsets für Haare

  // Berechne Vertex-Animation wenn vertices aktiv (auch ohne sichtbares Mesh für Haare)
  const currentLetter = letterSequence[currentLetterIndex];
  const meshDataForAnimation = currentMeshData || letterMeshData[currentLetter];
  const hairMapForAnimation = currentHairToVertexMap || letterHairToVertexMap[currentLetter];

  // Pre-Morph Deformation Fade-Out (vor dem Morph sanft ausblenden)
  if (isDeformFadingOut) {
    // Schnell ausblenden (ca. 0.3 Sekunden)
    deformFadeProgress -= deltaTime * 3.0;
    if (deformFadeProgress <= 0.0) {
      deformFadeProgress = 0.0;
      isDeformFadingOut = false;
      // Jetzt den eigentlichen Morph starten
      if (pendingMorphIndex >= 0) {
        console.log("Deformation faded out, starting morph...");
        startMorphTo(pendingMorphIndex);
      }
    }
  }

  // Während Morph: Deformation bleibt bei 0
  if (isMorphing) {
    deformFadeProgress = 0.0;
    isDeformFading = false;
  }

  // Post-Morph Deformation Fade-In (nach Morph langsam einblenden)
  if (isDeformFading && !isMorphing && !isDeformFadingOut) {
    // Langsam einblenden (ca. 1.5 Sekunden)
    deformFadeProgress += deltaTime * 0.7;
    if (deformFadeProgress >= 1.0) {
      deformFadeProgress = 1.0;
      isDeformFading = false;
    }
  }

  if (meshDataForAnimation && settings.vertices && settings.mouseDeform && deformFadeProgress > 0.01) {
    // Maus-Intensität
    let verticesIntensity = settings.verticesIntensity;
    if (settings.mouseIntensity) {
      verticesIntensity = mapY;
    }
    // Deformation mit Fade-Faktor multiplizieren (sanftes Easing)
    const easedFade = deformFadeProgress * deformFadeProgress * (3 - 2 * deformFadeProgress);
    verticesIntensity *= easedFade;

    const { positionClone, normalClone, countVertices } = meshDataForAnimation;

    if (positionClone) {
      // Array für Vertex-Deformationen anlegen (für Haar-Sync)
      vertexDeformations = new Float32Array(countVertices * 3);

      // Prüfe auf UVs nur wenn Mesh sichtbar ist
      const geometry = currentLetterMesh ? currentLetterMesh.geometry : null;
      const hasUV = geometry && geometry.attributes.uv && geometry.attributes.uv.count > 0;

      for (let i = 0; i < countVertices; i++) {
        // Berechne Wave-Parameter
        let uX, uY;

        if (hasUV) {
          // Mit UV-Koordinaten
          uX = geometry.attributes.uv.getX(i) * Math.PI * AMOUNT;
          uY = geometry.attributes.uv.getY(i) * Math.PI * AMOUNT;
        } else {
          // Fallback ohne UVs: Verwende Position als Basis
          // Normalisierte Position im Raum für organische Wellen
          const px = positionClone[i * 3];
          const py = positionClone[i * 3 + 1];
          const pz = positionClone[i * 3 + 2];

          // Verwende Position für Wellen (skaliert)
          uX = (px * 2.0 + py * 0.5) * Math.PI * AMOUNT * 0.5;
          uY = (pz * 2.0 + py * 0.5) * Math.PI * AMOUNT * 0.5;
        }

        const xsin = Math.sin(uX + now) * DAMPING;
        const ycos = Math.cos(uY + now) * DAMPING;
        const waveOffset = (xsin + ycos) * 0.1 * verticesIntensity;

        const ix = i * 3;
        const iy = i * 3 + 1;
        const iz = i * 3 + 2;

        const nx = normalClone ? normalClone[ix] : 0;
        const ny = normalClone ? normalClone[iy] : 1;
        const nz = normalClone ? normalClone[iz] : 0;

        // Deformation berechnen
        const dx = nx * waveOffset;
        const dy = ny * waveOffset;
        const dz = nz * waveOffset;

        // Speichern für Haar-Sync
        vertexDeformations[ix] = dx;
        vertexDeformations[iy] = dy;
        vertexDeformations[iz] = dz;

        // Nur Mesh aktualisieren wenn sichtbar
        if (currentLetterMesh && geometry) {
          geometry.attributes.position.setX(i, positionClone[ix] + dx);
          geometry.attributes.position.setY(i, positionClone[iy] + dy);
          geometry.attributes.position.setZ(i, positionClone[iz] + dz);
        }
      }

      // Nur Mesh aktualisieren wenn sichtbar
      if (currentLetterMesh && geometry) {
        geometry.computeVertexNormals();
        geometry.attributes.position.needsUpdate = true;
      }
    }
  } else if (currentLetterMesh && meshDataForAnimation && !isMorphing) {
    // Wenn keine Deformation aktiv, Mesh auf Original-Positionen zurücksetzen
    // (wichtig damit es nicht in deformiertem Zustand "einfriert")
    const geometry = currentLetterMesh.geometry;
    const { positionClone, countVertices } = meshDataForAnimation;
    if (geometry && positionClone) {
      for (let i = 0; i < countVertices; i++) {
        const ix = i * 3;
        const iy = i * 3 + 1;
        const iz = i * 3 + 2;
        geometry.attributes.position.setX(i, positionClone[ix]);
        geometry.attributes.position.setY(i, positionClone[iy]);
        geometry.attributes.position.setZ(i, positionClone[iz]);
      }
      geometry.computeVertexNormals();
      geometry.attributes.position.needsUpdate = true;
    }
  }

  // Post-Morph Fade-In (neues Mesh blendet NACH dem Morph langsam von 0% auf 100% ein)
  if (isPostMorphFading && currentLetterMesh && currentLetterMesh.material) {
    // Mesh sichtbar machen (falls noch nicht)
    currentLetterMesh.visible = true;

    // Langsam einblenden (ca. 2 Sekunden)
    postMorphFadeProgress += deltaTime * 0.5;

    if (postMorphFadeProgress >= 1.0) {
      postMorphFadeProgress = 1.0;
      isPostMorphFading = false;
      currentLetterMesh.material.opacity = 1.0;
    } else {
      // Von 0% zu 100% mit sanftem Easing
      const eased = postMorphFadeProgress * postMorphFadeProgress * (3 - 2 * postMorphFadeProgress);
      currentLetterMesh.material.opacity = eased;
    }
  }

  // Morphing Animation
  if (isMorphing) {
    morphProgress += deltaTime * MORPH_SPEED;

    // Mesh-Fade Animation (sequentiell: erst ausblenden, dann einblenden)
    if (settings.particleMorphShowMesh) {
      // Altes Mesh schnell ausblenden (in den ersten 30% des Morphs)
      if (currentLetterMesh && currentLetterMesh.material) {
        const fadeOutProgress = Math.min(morphProgress / 0.3, 1.0);
        // Smooth easing
        const fadeOut = fadeOutProgress * fadeOutProgress * (3 - 2 * fadeOutProgress);
        currentLetterMesh.material.opacity = 1.0 - fadeOut;

        // Verstecken wenn komplett ausgeblendet
        if (fadeOut >= 1.0) {
          currentLetterMesh.visible = false;
        }
      }

      // Neues Mesh bleibt komplett unsichtbar während des Morphs!
      // Es wird erst NACH dem Morph eingeblendet (im Post-Morph Fade)
      if (targetLetterMesh && targetLetterMesh.material) {
        // Rotation synchronisieren (für später)
        targetLetterMesh.rotation.y = hairMesh ? hairMesh.rotation.y : 0;
        // Komplett unsichtbar halten
        targetLetterMesh.material.opacity = 0;
        targetLetterMesh.visible = false;  // Auch visible auf false
      }
    }

    if (morphProgress >= 1.0) {
      morphProgress = 1.0;
      isMorphing = false;
      currentLetterIndex = targetLetterIndex;
      console.log(`Now showing: ${letterSequence[currentLetterIndex]}`);

      // Letter-Mesh wechseln wenn sichtbar
      if (settings.particleMorphShowMesh) {
        // Altes Mesh Opacity zurücksetzen (ist schon versteckt)
        if (currentLetterMesh && currentLetterMesh !== targetLetterMesh) {
          currentLetterMesh.material.opacity = 1.0;
        }

        // Aktuelles Mesh aktualisieren
        currentLetterMesh = targetLetterMesh;
        currentMeshData = targetMeshData;
        currentHairToVertexMap = letterHairToVertexMap[letterSequence[currentLetterIndex]] || null;

        // Post-Morph Fade starten (Mesh blendet von 0% auf 100% ein)
        if (currentLetterMesh) {
          postMorphFadeProgress = 0;
          isPostMorphFading = true;
        }

        // Target zurücksetzen
        targetLetterMesh = null;
        targetMeshData = null;
      }

      // Post-Morph Deformation Fade starten (Verformung blendet langsam ein)
      deformFadeProgress = 0;
      isDeformFading = true;
    }

    // Update Root-Positionen und Normalen
    const targetLetter = letterSequence[targetLetterIndex];
    const targetPos = letterPositions[targetLetter];
    const targetNorm = letterNormals[targetLetter];

    if (targetPos) {
      for (let hair = 0; hair < HAIR_COUNT; hair++) {
        // Verzögerung pro Haar
        const delay = morphDelays[hair];
        let t = Math.max(0, (morphProgress - delay) / (1 - delay));

        // Smooth easing (ease-in-out)
        t = t * t * (3 - 2 * t);

        // Kurven-Bewegung: Peak in der Mitte
        const midT = Math.sin(t * Math.PI);

        // Start
        const sx = startRootPositions[hair * 3];
        const sy = startRootPositions[hair * 3 + 1];
        const sz = startRootPositions[hair * 3 + 2];

        // Ziel
        const tx = targetPos[hair * 3];
        const ty = targetPos[hair * 3 + 1];
        const tz = targetPos[hair * 3 + 2];

        // Lineare Interpolation + Kurven-Auslenkung
        currentRootPositions[hair * 3] = sx + (tx - sx) * t + curveVelocities[hair * 3] * midT;
        currentRootPositions[hair * 3 + 1] = sy + (ty - sy) * t + curveVelocities[hair * 3 + 1] * midT;
        currentRootPositions[hair * 3 + 2] = sz + (tz - sz) * t + curveVelocities[hair * 3 + 2] * midT;

        // Normalen interpolieren
        if (targetNorm) {
          const snx = startRootNormals[hair * 3];
          const sny = startRootNormals[hair * 3 + 1];
          const snz = startRootNormals[hair * 3 + 2];

          const tnx = targetNorm[hair * 3];
          const tny = targetNorm[hair * 3 + 1];
          const tnz = targetNorm[hair * 3 + 2];

          currentRootNormals[hair * 3] = snx + (tnx - snx) * t;
          currentRootNormals[hair * 3 + 1] = sny + (tny - sny) * t;
          currentRootNormals[hair * 3 + 2] = snz + (tnz - snz) * t;
        }
      }
    }
  }

  // Update Haar-Attribute (mit Vertex-Deformation wenn vorhanden)
  // Nutze das Haar-Mapping auch wenn Mesh nicht sichtbar
  const activeHairMap = currentHairToVertexMap || hairMapForAnimation;
  updateHairAttributes(deltaTime, vertexDeformations, activeHairMap);
}

/**
 * Aktualisiert die Haar-Attribute für den Shader
 * @param {number} deltaTime - Zeit seit letztem Frame
 * @param {Float32Array|null} vertexDeformations - Vertex-Deformationen vom Mesh (für Haar-Sync)
 * @param {Int32Array|null} hairToVertexMap - Mapping von Haar-Index zu Vertex-Index
 */
function updateHairAttributes(deltaTime, vertexDeformations = null, hairToVertexMap = null) {
  if (!rootPositionAttr || !rootNormalAttr || !velocityAttr) return;

  const rootPos = rootPositionAttr.array;
  const rootNorm = rootNormalAttr.array;
  const velArray = velocityAttr.array;

  const dt = Math.max(deltaTime, 0.001);

  // Prüfe ob wir Vertex-Deformationen anwenden können
  // Das funktioniert nur wenn: nicht morphing und Deformationen + Mapping vorhanden
  const canApplyDeformation = !isMorphing && vertexDeformations && hairToVertexMap;

  for (let hair = 0; hair < HAIR_COUNT; hair++) {
    // Basis-Position aus currentRootPositions
    let px = currentRootPositions[hair * 3] + hairPositionOffsets[hair * 3];
    let py = currentRootPositions[hair * 3 + 1] + hairPositionOffsets[hair * 3 + 1];
    let pz = currentRootPositions[hair * 3 + 2] + hairPositionOffsets[hair * 3 + 2];

    // Wenn Vertex-Deformation vorhanden, addiere die Deformation des zugehörigen Vertex
    if (canApplyDeformation) {
      const vertexIdx = hairToVertexMap[hair];
      if (vertexIdx >= 0 && vertexIdx * 3 + 2 < vertexDeformations.length) {
        px += vertexDeformations[vertexIdx * 3];
        py += vertexDeformations[vertexIdx * 3 + 1];
        pz += vertexDeformations[vertexIdx * 3 + 2];
      }
    }

    // Normale (mit Offset und Normalisierung)
    let nx = currentRootNormals[hair * 3] + hairNormalOffsets[hair * 3];
    let ny = currentRootNormals[hair * 3 + 1] + hairNormalOffsets[hair * 3 + 1];
    let nz = currentRootNormals[hair * 3 + 2] + hairNormalOffsets[hair * 3 + 2];
    const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nLen > 0) {
      nx /= nLen; ny /= nLen; nz /= nLen;
    }

    // Velocity berechnen
    const lastX = lastRootPositions[hair * 3];
    const lastY = lastRootPositions[hair * 3 + 1];
    const lastZ = lastRootPositions[hair * 3 + 2];

    let velX = (px - lastX) / dt;
    let velY = (py - lastY) / dt;
    let velZ = (pz - lastZ) / dt;

    // Velocity dämpfen
    const dampFactor = 0.7;
    hairVelocities[hair * 3] = hairVelocities[hair * 3] * dampFactor + velX * (1 - dampFactor);
    hairVelocities[hair * 3 + 1] = hairVelocities[hair * 3 + 1] * dampFactor + velY * (1 - dampFactor);
    hairVelocities[hair * 3 + 2] = hairVelocities[hair * 3 + 2] * dampFactor + velZ * (1 - dampFactor);

    // Speichern für nächsten Frame
    lastRootPositions[hair * 3] = px;
    lastRootPositions[hair * 3 + 1] = py;
    lastRootPositions[hair * 3 + 2] = pz;

    // Für alle Segmente des Haars
    for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
      const idx = hair * SEGMENTS_PER_HAIR + seg;

      rootPos[idx * 3] = px;
      rootPos[idx * 3 + 1] = py;
      rootPos[idx * 3 + 2] = pz;

      rootNorm[idx * 3] = nx;
      rootNorm[idx * 3 + 1] = ny;
      rootNorm[idx * 3 + 2] = nz;

      velArray[idx * 3] = hairVelocities[hair * 3];
      velArray[idx * 3 + 1] = hairVelocities[hair * 3 + 1];
      velArray[idx * 3 + 2] = hairVelocities[hair * 3 + 2];
    }
  }

  rootPositionAttr.needsUpdate = true;
  rootNormalAttr.needsUpdate = true;
  velocityAttr.needsUpdate = true;
}

/**
 * Getter
 */
export function getParticleSystem() {
  return hairMesh;
}

export function getCurrentLetter() {
  return letterSequence[currentLetterIndex] || "";
}

export function getCurrentLetterIndex() {
  return currentLetterIndex;
}

export function getLetterSequence() {
  return letterSequence;
}

export function getText() {
  return textSequence;
}

export function getIsMorphing() {
  return isMorphing;
}

export function isParticleMorphLoaded() {
  return isLoaded;
}

/**
 * Setzt die Haar-Farben (Root und/oder Tip)
 */
export function setHairColors(rootColor, tipColor) {
  if (uniforms) {
    if (rootColor) {
      uniforms.uColorRoot.value = new THREE.Color(rootColor);
      HAIR_COLOR_ROOT = rootColor;
      settings.particleMorphHairColorRoot = rootColor;
    }
    if (tipColor) {
      uniforms.uColorTip.value = new THREE.Color(tipColor);
      HAIR_COLOR_TIP = tipColor;
      settings.particleMorphHairColorTip = tipColor;
    }
  }
}

/**
 * Setzt die Haar-Länge
 */
export function setHairLength(length) {
  HAIR_LENGTH = length;
  settings.particleMorphHairLength = length;
  if (uniforms) {
    uniforms.uHairLength.value = length;
  }
}

/**
 * Setzt die Biegestärke
 */
export function setBendStrength(strength) {
  BEND_STRENGTH = strength;
  settings.particleMorphHairBendStrength = strength;
  if (uniforms) {
    uniforms.uBendStrength.value = strength;
  }
}

/**
 * Aktiviert/Deaktiviert Schwerkraft
 */
export function setGravityEnabled(enabled) {
  settings.particleMorphHairGravity = enabled;
  if (uniforms) {
    uniforms.uGravityEnabled.value = enabled ? 1.0 : 0.0;
  }
}

/**
 * Setzt die Schwerkraft-Stärke
 */
export function setGravityStrength(strength) {
  GRAVITY_STRENGTH = strength;
  settings.particleMorphHairGravityStrength = strength;
  if (uniforms) {
    uniforms.uGravityStrength.value = strength;
  }
}

/**
 * Setzt die Mesh-Farbe für alle Letter-Meshes
 */
export function setMeshColor(color) {
  settings.particleMorphMeshColor = color;
  const threeColor = new THREE.Color(color);

  Object.values(letterMeshes).forEach(mesh => {
    if (mesh && mesh.material) {
      mesh.material.color = threeColor;
    }
  });
}

/**
 * Setzt die Haar-Anzahl (erfordert Neuerstellen)
 */
export function setParticleCount(count) {
  HAIR_COUNT = Math.max(1000, Math.min(count, 100000));
  settings.particleMorphHairCount = HAIR_COUNT;
  console.log(`Hair count set to: ${HAIR_COUNT}`);
}

/**
 * Cleanup
 */
export function removeParticleMorph() {
  if (hairMesh) {
    scene.remove(hairMesh);
    hairGeometry.dispose();
    hairMesh.material.dispose();
    hairMesh = null;
  }
  isLoaded = false;
  currentLetterIndex = 0;
  targetLetterIndex = 0;
}

/**
 * Trigger für Dissolve
 */
export function triggerParticleDissolve() {
  console.log("Hair dissolve triggered");
  // TODO: Implementiere Haar-Explosion
}
